// @flow
import React, { useCallback, useState, useContext, useMemo } from 'react';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import isEmpty from 'ramda/es/isEmpty';
import assoc from 'ramda/es/assoc';
import find from 'ramda/es/find';
import reduce from 'ramda/es/reduce';
import reduced from 'ramda/es/reduced';
import evolve from 'ramda/es/evolve';
import values from 'ramda/es/values';
import omit from 'ramda/es/omit';

import { PlainFilterComplete } from 'app/common/ui/PlainFilter';
import PlainButton from 'app/common/ui-next/plain-button';
import ControlsBar from 'app/common/ui-next/controls-bar';
import PlainInput from 'app/common/ui/PlainInput';
import PlainTextArea from 'app/common/ui/PlainTextArea';
import { PlainColumns, PlainColumn } from 'app/common/ui/PlainGrid';
import { type FilesType } from 'app/common/api';

import useSchools from '../useSchools';
import useStatementEditFormErrors from './useStatementEditFormErrors';
import { RecipientsContext, useActualSchoolsByRecipients } from '../RecipientsData';
import AddFiles from './AddFiles';
import styles from './StatementEditForm.scss';


const cx = classNames.bind(styles);
const formId = 'submitStatementForm';

export type FormData = {
  schoolId: [number] | [],
  recipientIds: Array<number>,
  subject: string,
  content: string,
  files: FilesType,
}

const defaultFormData: FormData = {
  schoolId: [],
  recipientIds: [],
  subject: '',
  content: '',
  files: {},
};

export const availableSelectors = ['schoolId', 'recipientIds'];
export const availableInputs = ['subject', 'content'];
const maxChars = { subject: 60, content: 500 };


/**
 *  ФОРМА СОЗДАНИЯ/РЕДАКТИРОВАНИЯ ОБРАЩЕНИЯ
 */

type Props = {
  onSubmit: (data: FormData) => Promise<void>,
  onCancel: () => void,
  initialFormData?: FormData,
  saving?: boolean,
}

const StatementEditForm = ({
  onSubmit,
  onCancel,
  initialFormData = null,
  saving,
}: Props) => {
  const { t } = useTranslation('statements');
  const [formData, setFormData] = useState<FormData>(() => (initialFormData || defaultFormData));
  const [errors, handleSetError, handleResetErrors] = useStatementEditFormErrors();

  const [schools,, loadingSchools] = useSchools();
  const [
    recipients,
    loadingRecipients,
  ] = useContext(RecipientsContext);

  // только те школы, у которых есть получатели
  const schoolsActual = useActualSchoolsByRecipients(schools, recipients);


  const handleChangeSelected = useCallback(({ id, value }: { id: string, value: Array<number> }) => {
    if (availableSelectors.includes(id)) {
      setFormData(assoc(id, value));
      handleResetErrors();
    }
  }, [handleResetErrors]);


  const handleChangeInput = useCallback((e: SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    if (availableInputs.includes(name)) {
      setFormData(assoc(name, value));
      handleResetErrors();
    }
  }, [handleResetErrors]);


  const handleAddFiles = useCallback((files: FilesType) => {
    setFormData(evolve({ files: items => ({ ...items, ...files }) }));
  }, []);

  const handleRemoveFile = useCallback((id: string) => {
    setFormData(evolve({ files: omit([id]) }));
  }, []);


  const validateFields = useCallback(() => {
    const { schoolId, recipientIds } = formData;
    if (isEmpty(schoolId)) {
      handleSetError('schoolId', t('create_statement_form_error_school'));
      return false;
    }
    if (isEmpty(recipientIds)) {
      handleSetError('recipientIds', t('create_statement_form_error_recipients'));
      return false;
    }

    const inputEmptyField = find(inputName => !formData[inputName], availableInputs);
    const overflowField = find(
      inputName => formData[inputName].length > maxChars[inputName],
      availableInputs
    );

    // ищем ближайшее поле с ошибкой
    if (inputEmptyField || overflowField) {
      const [errorText, errorField] = reduce((acc, inputName) => {
        if (inputName === inputEmptyField) {
          return reduced([t('create_statement_form_error_empty'), inputName]);
        }
        if (inputName === overflowField) {
          return reduced([t('create_statement_form_error_over'), inputName]);
        }
        return acc;
      }, [], availableInputs);
      handleSetError(errorField, errorText);
      return false;
    }

    return true;
  }, [formData, t, handleSetError]);


  const handleSubmit = useCallback((e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const id = e.target instanceof HTMLFormElement ? e.target.id : null;
    if (id !== formId) return;

    if (validateFields()) {
      onSubmit(formData);
    }
  }, [formData, onSubmit, validateFields]);


  const {
    schoolId,
    recipientIds,
    subject,
    content,
  } = formData;

  const addedFilesArray = useMemo(() => values(formData.files), [formData.files]);

  return (
    <form onSubmit={handleSubmit} id={formId}>
      <PlainFilterComplete
        id="schoolId"
        className={cx(styles.selector, {
          withErrorFiller: !errors.schoolId && isEmpty(schoolId),
        })}
        title={t('create_statement_form_school_label')}
        modalTitle={t('create_statement_form_school_select_modal_header')}
        itemsObj={schoolsActual}
        selectedArr={schoolId}
        onChange={handleChangeSelected}
        light={false}
        selectedAllStateEnabled={false}
        singleSelectable
        hideNoData
        loading={loadingSchools || loadingRecipients}
        errorText={errors.schoolId}
        disabled={saving}
      />

      {schoolId
        && !isEmpty(schoolId)
        && <PlainFilterComplete
          id="recipientIds"
          className={cx(styles.selector, {
            withErrorFiller: !errors.recipientIds && isEmpty(recipientIds),
          })}
          title={t('create_statement_form_recipients_label')}
          modalTitle={t('create_statement_form_recipients_select_modal_header')}
          itemsObj={recipients}
          selectedArr={recipientIds}
          onChange={handleChangeSelected}
          light={false}
          selectedAllStateEnabled={false}
          hideNoData
          loading={loadingRecipients}
          errorText={errors.recipientIds}
          disabled={saving}
        />}

      <PlainColumns>
        <PlainColumn div={2}>
          <PlainInput
            label={t('subject_name_field_label')}
            name="subject"
            value={subject}
            onChange={handleChangeInput}
            disabled={saving}
            isError={!!errors.subject}
            errorText={errors.subject}
            maxChars={maxChars.subject}
            placeholder={t('subject_name_field_placeholder')}
          />
        </PlainColumn>
      </PlainColumns>

      <PlainColumns>
        <PlainColumn div={2}>
          <PlainTextArea
            label={t('content_field_label')}
            name="content"
            value={content}
            onChange={handleChangeInput}
            disabled={saving}
            isError={!!errors.content}
            errorText={errors.content}
            maxChars={maxChars.content}
            placeholder={t('content_field_placeholder')}
          />
        </PlainColumn>
      </PlainColumns>

      <PlainColumns>
        <PlainColumn div={2}>
          <AddFiles
            disabled={saving}
            onAddFiles={handleAddFiles}
            onRemoveFile={handleRemoveFile}
            files={addedFilesArray}
          />
        </PlainColumn>
      </PlainColumns>

      <ControlsBar className={styles.bottomControlsBar}>
        <PlainButton
          type="submit"
          disabled={saving}
        >
          {(t('create_statement_form_submit_bt'))}
        </PlainButton>
        <PlainButton type="button" onClick={onCancel}>
          {(t('create_statement_form_cancel_bt'))}
        </PlainButton>
      </ControlsBar>
    </form>
  );
};

export default StatementEditForm;
